import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const NEVER = "brynai-install-never";
const UNTIL = "brynai-install-hide-until";
let deferred = null;
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; window.dispatchEvent(new Event("brynai-installable")); });

const ua = navigator.userAgent || "";
const isIOS = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
const isAndroid = /Android/.test(ua);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

function shouldShow() {
  if (!(isIOS || isAndroid) || standalone()) return false;
  if (localStorage.getItem(NEVER)) return false;
  return Date.now() > Number(localStorage.getItem(UNTIL) || 0);
}

function Phone({ children }) {
  return (
    <svg viewBox="0 0 120 200" className="h-40 w-auto shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="108" height="192" rx="16" className="text-neutral-500" />
      <rect x="16" y="22" width="88" height="140" rx="4" className="text-neutral-700" />
      {children}
    </svg>
  );
}

const IosDiagram = () => (
  <Phone>
    <g className="text-sky-300">
      <rect x="44" y="170" width="32" height="18" rx="4" strokeDasharray="3 2" />
      <path d="M60 184v-10M55 178l5-5 5 5" />
      <circle cx="96" cy="178" r="9" className="text-yellow-300" />
      <text x="93" y="182" fontSize="10" fill="currentColor" stroke="none" className="text-yellow-300">1</text>
    </g>
    <g className="text-neutral-300">
      <rect x="24" y="100" width="72" height="16" rx="3" />
      <path d="M30 108h6M33 105v6" />
      <text x="40" y="111" fontSize="7" fill="currentColor" stroke="none">Add to Home Screen</text>
      <circle cx="104" cy="108" r="7" className="text-yellow-300" />
      <text x="101.5" y="111" fontSize="8" fill="currentColor" stroke="none" className="text-yellow-300">2</text>
    </g>
  </Phone>
);

const AndroidDiagram = () => (
  <Phone>
    <g className="text-sky-300">
      <circle cx="96" cy="30" r="1.5" fill="currentColor" /><circle cx="96" cy="35" r="1.5" fill="currentColor" /><circle cx="96" cy="40" r="1.5" fill="currentColor" />
      <rect x="89" y="25" width="14" height="20" rx="3" strokeDasharray="3 2" />
      <circle cx="80" cy="35" r="7" className="text-yellow-300" />
      <text x="77.5" y="38" fontSize="8" fill="currentColor" stroke="none" className="text-yellow-300">1</text>
    </g>
    <g className="text-neutral-300">
      <rect x="40" y="52" width="60" height="44" rx="3" />
      <path d="M46 64h40M46 76h44" className="text-neutral-600" />
      <rect x="43" y="82" width="54" height="11" rx="2" className="text-sky-300" />
      <text x="47" y="90" fontSize="6.5" fill="currentColor" stroke="none" className="text-sky-300">Install app</text>
      <circle cx="30" cy="88" r="7" className="text-yellow-300" />
      <text x="27.5" y="91" fontSize="8" fill="currentColor" stroke="none" className="text-yellow-300">2</text>
    </g>
  </Phone>
);

const STEPS = {
  ios: ["Tap the Share button at the bottom of Safari (the square with an arrow).", "Scroll and choose \"Add to Home Screen\".", "Tap \"Add\" in the top corner."],
  android: ["Tap the ⋮ menu at the top right of Chrome.", "Choose \"Install app\" or \"Add to Home screen\".", "Confirm by tapping \"Install\" or \"Add\"."],
};

export function InstallCard() {
  const [show, setShow] = useState(shouldShow);
  const [canPrompt, setCanPrompt] = useState(!!deferred);
  useEffect(() => {
    const h = () => setCanPrompt(true);
    window.addEventListener("brynai-installable", h);
    return () => window.removeEventListener("brynai-installable", h);
  }, []);
  if (!show) return null;
  const os = isIOS ? "ios" : "android";
  const later = () => { localStorage.setItem(UNTIL, String(Date.now() + 7 * 864e5)); setShow(false); };
  const never = () => { localStorage.setItem(NEVER, "1"); setShow(false); };
  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    deferred = null;
    setCanPrompt(false);
    if (outcome === "accepted") never();
  };
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden" data-testid="install-card">
      <div className="rounded-3xl border border-white/10 bg-[#121725]/95 backdrop-blur-xl p-5 shadow-2xl rise">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/icon-192.png" alt="" className="h-11 w-11 rounded-xl" />
            <div><p className="font-display font-semibold">Add BrynAI to your home screen</p><p className="text-xs text-neutral-400">Opens full-screen, like an app.</p></div>
          </div>
          <button onClick={later} className="p-1 text-neutral-500" data-testid="install-close-btn"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-4 mt-4 items-center">
          {os === "ios" ? <IosDiagram /> : <AndroidDiagram />}
          <ol className="space-y-2 text-sm text-neutral-300" data-testid="install-steps">
            {STEPS[os].map((t, i) => (
              <li key={t} className="flex gap-2"><span className="h-5 w-5 shrink-0 rounded-full bg-yellow-300 text-black text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>{t}</li>
            ))}
          </ol>
        </div>
        {canPrompt && (
          <button onClick={install} className="mt-4 w-full h-11 rounded-full bg-sky-400 text-white font-medium flex items-center justify-center gap-2" data-testid="install-now-btn">
            <Download className="h-4 w-4" /> Install
          </button>
        )}
        <div className="flex justify-between mt-3 text-xs">
          <button onClick={later} className="text-neutral-400 py-2" data-testid="install-later-btn">Not now</button>
          <button onClick={never} className="text-neutral-500 py-2" data-testid="install-never-btn">Don't show again</button>
        </div>
      </div>
    </div>
  );
}
